import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import {
  Slider,
  Button,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import BusinessIcon from "@material-ui/icons/Business";
import getCroppedImg from "../../helpers/GetCroppedImg";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  circle: {
    width: 150,
    height: 150,
    borderRadius: "50%",
    overflow: "hidden",
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fafafa",
    border: "1px solid #e0e0e0",
    cursor: "pointer",
  },
  placeholderIcon: {
    fontSize: 64,
    color: "#9e9e9e",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  editIcon: {
    color: "#fff",
    fontSize: 28,
  },
  dropHint: {
    position: "absolute",
    bottom: 8,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "#fff",
    fontSize: 12,
  },
  cropContainer: {
    position: "relative",
    width: "100%",
    height: 400,
    background: "#333",
  },
}));

const LogoUploader = ({ onChange, initialLogoUrl }) => {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isNewUpload, setIsNewUpload] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setIsNewUpload(true);

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result);
        setOpenModal(true);
      });
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    onDropRejected: (rejectedFiles) => {
      if (rejectedFiles && rejectedFiles.length > 0) {
        const firstFile = rejectedFiles[0];
        const firstError = firstFile.errors[0];

        let errorMessage = "Ocurrió un error al subir el archivo.";

        if (firstError.code === 'file-invalid-type') {
          errorMessage = "Formato de archivo no válido. Solo se aceptan imágenes JPG, PNG o WEBP.";
        }

        toast.error(errorMessage);
      }
    }
  });

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const fileName = selectedFile ? selectedFile.name : `logo_${Date.now()}.jpg`;
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, fileName);
      onChange && onChange(croppedFile);
      setSelectedFile(croppedFile);
      setImageSrc(URL.createObjectURL(croppedFile));
      setOpenModal(false);
      setIsNewUpload(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = () => {
    setOpenModal(false);
    if (isNewUpload) {
      setSelectedFile(null);
      setImageSrc(null);
      setIsNewUpload(false);
    }
  };

  const handleCircleClick = (e) => {
    e.preventDefault();
    if (imageSrc) {
      setOpenModal(true);
      setIsNewUpload(false);
    } else {
      open();
    }
  };

  return (
    <Box>
      <div
        {...getRootProps()}
        style={{ display: "inline-block" }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <input {...getInputProps()} />

        <div
          className={classes.circle}
          onClick={handleCircleClick}
          role="button"
          aria-label="Logo uploader"
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt="Logo preview"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : initialLogoUrl ? (
            <img
              src={initialLogoUrl}
              alt="Logo actual"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <BusinessIcon className={classes.placeholderIcon} />
          )}
          {(hovered || isDragActive) && (
            <div className={classes.overlay}>
              <div style={{ textAlign: "center" }}>
                <EditIcon className={classes.editIcon} />
                {isDragActive && (
                  <div className={classes.dropHint}>
                    Suelta la imagen para reemplazar
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={openModal} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogContent>
          {imageSrc ? (
            <>
              <div className={classes.cropContainer}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <Box style={{ marginTop: 12 }}>
                <Typography gutterBottom>Zoom</Typography>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e, value) => setZoom(value)}
                />
              </Box>

              <Box mt={2} textAlign="center">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    open();
                  }}
                >
                  Cambiar imagen
                </Button>
              </Box>
            </>
          ) : (
            <Box p={2} textAlign="center">
              <Typography>No hay imagen seleccionada.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSave}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default LogoUploader;
